import { useCallback, useMemo, useState } from 'react';

import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  Button,
  ButtonLink,
  Card,
  Checkbox,
  DataRow,
  ErrorState,
  Input,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { useUploadReports } from '@/features/validation/useUploadReport';
import { acknowledgeableIssues } from '@/features/validation/issues';
import { PriceBreakdown } from '@/features/pricing/PriceBreakdown';
import { useAnnounceOnChange } from '@/lib/a11y';
import { startCheckout } from '@/lib/api/checkout';
import { useMeta } from '@/lib/api/meta';
import { useIsDesktop } from '@/lib/hooks/useMediaQuery';
import { useApiMutation } from '@/lib/api/queryClient';
import type { QuoteOut } from '@/lib/api/types';
import { useQuote } from '@/lib/api/quotes';
import { formatDayRange, formatPaise } from '@/lib/format';

import { stepUrl } from './steps';
import { useOrderStep } from './useOrderStep';

/**
 * /order/pay — the last screen before money moves.
 *
 * Three things this screen is careful about:
 *
 * **The amount is re-read, never carried.** The total comes from GET /quotes/{id}
 * at render time and goes back to the server as `expected_total_paise` purely so
 * it can be checked. If the catalogue moved underneath the customer, the server
 * answers `price_mismatch` and we send them back to Review with the new price
 * rather than quietly charging either number.
 *
 * **Warnings are confirmed here, not remembered from earlier.** Review asks for
 * an acknowledgement to let the customer move on; this asks again at the moment
 * of purchase, because that is the confirmation the shop would point to in a
 * "you printed my blurry scan" dispute. Carrying the earlier ticks in the URL
 * would make a pre-acknowledged link shareable, which is exactly wrong.
 *
 * **Leaving for the gateway settles nothing.** A successful POST hands us a
 * `checkout_url` and we go there. The order becomes paid when the signed webhook
 * arrives — the return page reads state rather than believing the redirect.
 */
/** The customer's own filename for one document, or its position as a fallback. */
function documentLabel(quote: QuoteOut, uploadId: string): string {
  const index = quote.items.findIndex((item) => item.upload_id === uploadId);
  if (index === -1) return 'This document';
  return quote.items[index]?.filename ?? `Document ${index + 1}`;
}

export function PayStep() {
  const { step, stepper, urlState } = useOrderStep();

  const quoteId = urlState.quote;
  const addressId = urlState.address;
  const quote = useQuote(quoteId);
  const isPickup = quote.data?.fulfilment_method === 'pickup';
  const meta = useMeta();
  const isDesktop = useIsDesktop();

  // Back to Upload, carrying the quote (the basket) but NOT this document's
  // ids: a fresh file gets its own options rather than inheriting the last
  // one's. The basket comes back through `?q=`.
  const addDocumentUrl = stepUrl('upload', {
    quote: urlState.quote,
    upload: undefined,
    copies: undefined,
    binding: undefined,
    cover: undefined,
    address: undefined,
  });
  const maxDocuments = meta.data?.basket.max_documents ?? 10;
  const canAddDocument = (quote.data?.items.length ?? 0) < maxDocuments;

  // Every document's report, not just the one the URL happens to name: a
  // customer accepting a low-DPI scan in one file has said nothing about the
  // margins in another, and the server refuses the checkout unless each file's
  // own warnings are accepted.
  const uploadIds = useMemo(
    () => (quote.data?.items ?? []).map((item) => item.upload_id),
    [quote.data],
  );
  const reports = useUploadReports(uploadIds);

  const documentWarnings = useMemo(
    () =>
      reports.map(({ uploadId, report }) => ({
        uploadId,
        issues: report ? acknowledgeableIssues(report) : [],
      })),
    [reports],
  );
  const warnings = useMemo(
    () => documentWarnings.flatMap((entry) => entry.issues),
    [documentWarnings],
  );

  // Keyed by upload AND code: the same code on two files is two separate
  // confirmations, because they are two separate defects.
  const [confirmed, setConfirmed] = useState<ReadonlySet<string>>(() => new Set<string>());

  // Not in the URL, unlike the rest of this flow's state: a GSTIN is a
  // business identifier, and a shareable link carrying one is a leak.
  const [wantsGstInvoice, setWantsGstInvoice] = useState(false);
  const [gstin, setGstin] = useState('');
  const [businessName, setBusinessName] = useState('');
  const confirmKey = useCallback((uploadId: string, code: string) => `${uploadId}:${code}`, []);

  const toggleConfirm = useCallback((key: string, checked: boolean) => {
    setConfirmed((previous) => {
      const next = new Set(previous);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const allConfirmed = documentWarnings.every((entry) =>
    entry.issues.every((issue) => confirmed.has(confirmKey(entry.uploadId, issue.code))),
  );

  const checkout = useApiMutation({
    mutationFn: () => {
      if (!quoteId) throw new Error('no quote to pay for');
      return startCheckout({
        quote_id: quoteId,
        ...(quote.data ? { expected_total_paise: quote.data.total_paise } : {}),
        // A pickup has no address; sending a stale one has the checkout refused.
        ...(addressId && !isPickup ? { address_id: addressId } : {}),
        acknowledgements: documentWarnings.map((entry) => ({
          upload_id: entry.uploadId,
          codes: entry.issues.map((issue) => issue.code),
        })),
        ...(gstin.trim() ? { gstin: gstin.trim(), business_name: businessName.trim() } : {}),
      });
    },
    onSuccess: (response) => {
      // A full navigation, not a router push: the gateway is another origin and
      // the customer must be able to see its address bar.
      window.location.assign(response.checkout_url);
    },
  });

  const error = checkout.error;
  const priceMoved = error?.code === 'price_mismatch' || error?.code === 'quote_expired';

  useAnnounceOnChange(error ? error.message : null, error ? 'assertive' : 'polite');

  if (!quoteId) {
    return (
      <AppShell title={step.title} progress={stepper}>
        <ErrorState
          error={new Error('This order has no quote to pay for.')}
          title="We lost track of your order"
        />
        <ButtonLink to={stepUrl('upload', urlState)}>Back to the start</ButtonLink>
      </AppShell>
    );
  }

  if (quote.isLoading) {
    return (
      <AppShell title={step.title} progress={stepper}>
        <SkeletonGroup label="Loading your total">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </SkeletonGroup>
      </AppShell>
    );
  }

  if (quote.isError || !quote.data) {
    return (
      <AppShell title={step.title} progress={stepper}>
        <ErrorState
          error={quote.error}
          title="We could not load your total"
          onRetry={() => void quote.refetch()}
          retrying={quote.isFetching}
        />
      </AppShell>
    );
  }

  const total = quote.data.total_paise;
  // QuoteOut carries the amounts flat; PriceBreakdown wants the cart-summary
  // shape. Reshaped rather than recomputed — every number here is the server's.
  const quoteTotals = {
    sheets: quote.data.sheets,
    weight_g: quote.data.weight_g,
    print_cost_paise: quote.data.print_cost_paise,
    binding_cost_paise: quote.data.binding_cost_paise,
    subtotal_paise: quote.data.subtotal_paise,
    shipping_paise: quote.data.shipping_paise,
    shipping_waived_paise: 0,
    discount_paise: quote.data.discount_paise,
    tax_paise: quote.data.tax_paise,
    total_paise: quote.data.total_paise,
    total_display: quote.data.total_display,
  };

  return (
    <AppShell
      title={step.title}
      progress={stepper}
      aside={
        // Beside the button on desktop: this is the last screen before money
        // moves, and the amount belongs next to the thing that spends it
        // rather than a scroll above the warnings.
        <PriceBreakdown breakdown={quote.data.breakdown} totals={quoteTotals} defaultOpen />
      }
      action={
        <Button
          size="lg"
          fullWidth
          disabled={!allConfirmed || checkout.isPending}
          loading={checkout.isPending}
          onClick={() => checkout.mutate(undefined)}
        >
          {checkout.isPending ? 'Taking you to payment' : `Pay ${formatPaise(total)}`}
        </Button>
      }
    >
      <Card title="What you are paying for">
        {quote.data.items.map((item, index) => (
          <DataRow
            key={item.upload_id}
            label={
              quote.data.items.length > 1 ? (item.filename ?? `Document ${index + 1}`) : 'Pages'
            }
            value={`${item.page_count} × ${item.copies} ${item.copies === 1 ? 'copy' : 'copies'}`}
          />
        ))}
        <DataRow label="Sheets of paper" value={String(quote.data.sheets)} />
        {isPickup ? (
          <DataRow label="Collecting" value="From the shop" />
        ) : quote.data.pincode ? (
          <DataRow label="Delivering to" value={quote.data.pincode} />
        ) : null}
        {/* The last question a customer asks before paying, and until now the
            answer was "2-4 days" buried in the price breakdown. Server-computed
            dates: the shop's calendar decides this, not the device's clock. */}
        {quote.data.delivery_estimate ? (
          <DataRow
            label="Expected delivery"
            value={formatDayRange(
              quote.data.delivery_estimate.from_date,
              quote.data.delivery_estimate.to_date,
            )}
          />
        ) : null}

        {/* The last moment this is offered. After payment a second document is
            a second order, which costs the customer a second delivery. */}
        {canAddDocument ? (
          <div className="mt-3">
            <ButtonLink to={addDocumentUrl} variant="secondary" fullWidth>
              Add another document
            </ButtonLink>
            <p className="mt-2 text-sm text-ink-subtle">
              It goes in the same parcel, so you only pay delivery once.
            </p>
          </div>
        ) : (
          <p className="mt-3 text-sm text-ink-subtle">
            That is the most documents we can put in one order. Anything more needs a second order.
          </p>
        )}
      </Card>

      {!isDesktop ? (
        <PriceBreakdown breakdown={quote.data.breakdown} totals={quoteTotals} defaultOpen />
      ) : null}

      {/* B2B billing. Collapsed behind a checkbox because most customers are
          not businesses, and two always-visible fields they must ignore is two
          reasons to wonder whether they filled the form in wrongly. */}
      <Card title="Billing">
        <Checkbox
          name="is-business"
          checked={wantsGstInvoice}
          onChange={(event) => setWantsGstInvoice(event.target.checked)}
          label="I need a GST invoice for a business"
        />
        {wantsGstInvoice ? (
          <div className="mt-3 flex flex-col gap-3">
            <Input
              name="business_name"
              label="Registered business name"
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              required
              error={checkout.error?.fieldErrors?.['business_name']}
            />
            <Input
              name="gstin"
              label="GSTIN"
              value={gstin}
              onChange={(event) => setGstin(event.target.value.toUpperCase())}
              inputMode="text"
              spellCheck={false}
              required
              hint="15 characters, as on your registration certificate — e.g. 22AAAAA0000A1Z5."
              error={checkout.error?.fieldErrors?.['gstin']}
            />
          </div>
        ) : null}
      </Card>

      {warnings.length > 0 ? (
        <Card title="Please confirm before we print">
          <p className="text-sm text-muted">
            We flagged {warnings.length === 1 ? 'this' : 'these'} when we checked your
            {documentWarnings.filter((entry) => entry.issues.length).length > 1
              ? ' files'
              : ' file'}
            . Printing goes ahead as-is once you confirm, and we cannot refund sheets already
            printed.
          </p>
          {documentWarnings
            .filter((entry) => entry.issues.length > 0)
            .map((entry, index) => (
              <div key={entry.uploadId} className={index > 0 ? 'mt-4' : 'mt-3'}>
                {/* Attributed to the file it was raised against: with several
                    documents, "the scan is low quality" is meaningless until
                    the customer knows which scan. */}
                {documentWarnings.filter((other) => other.issues.length).length > 1 ? (
                  <p className="text-sm font-medium text-ink">
                    {documentLabel(quote.data, entry.uploadId)}
                  </p>
                ) : null}
                <ul className="mt-2 space-y-3">
                  {entry.issues.map((issue) => {
                    const key = confirmKey(entry.uploadId, issue.code);
                    return (
                      <li key={key}>
                        <Checkbox
                          name={`confirm-${key}`}
                          checked={confirmed.has(key)}
                          onChange={(event) => toggleConfirm(key, event.target.checked)}
                          label={issue.message}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
        </Card>
      ) : null}

      {error ? (
        <Alert
          severity={priceMoved ? 'warning' : 'danger'}
          title={priceMoved ? 'The price changed' : 'We could not start the payment'}
        >
          <p>{error.message}</p>
          {priceMoved ? (
            <p className="mt-2">
              <ButtonLink to={stepUrl('review', urlState)} variant="secondary">
                Review the new price
              </ButtonLink>
            </p>
          ) : (
            <p className="mt-2">
              <Button variant="secondary" onClick={() => checkout.mutate(undefined)}>
                Try again
              </Button>
            </p>
          )}
        </Alert>
      ) : null}

      <p className="mt-4 text-xs text-muted">
        You will be taken to our payment provider. Your order is only confirmed once the payment is
        verified — if you close that page by mistake, come back and try again.
      </p>
    </AppShell>
  );
}
